/**
 * Decisions driven by the live-status poll that are easy to get subtly wrong,
 * because they turn on the difference between "the response says no" and "the
 * response says nothing". The player component owns the DOM and the media
 * element; this module owns the rules.
 */

// LiveStreamStatus mirrors the server enum and already lives next to the
// waiting-viewer beacon that first needed it.
import { LiveStreamStatus } from './cmcd-beacon.ts';

/**
 * Fired by the poster image when it starts or stops having artwork to show,
 * with `detail.available`. Declared here rather than in either component so the
 * player does not have to import the poster module - that would pull the
 * poster's blurhash dependency into every graph that type-checks the player.
 */
export const POSTER_ARTWORK_STATE_EVENT = 'posterartworkstate';

type OfflineLiveStateInput = {
  showCountdown?: boolean;
  showStatus?: boolean;
  status?: number | null;
};

/**
 * True when a non-playable live response is definite enough to tear down an
 * already attached source.
 *
 * `Unknown` means the control plane could not resolve the stream's state, which
 * is not evidence that its source went away - tearing down there would
 * interrupt a stream that is still playing over a transient blip. A visible
 * countdown or status overlay is always definite: the viewer is already looking
 * at an offline screen. A response with no status at all (a local re-sync from
 * the controller's own dataset rather than the API) falls back to the overlays.
 *
 * Callers must have established that the stream is not playable; this only
 * answers whether the response can be trusted to mean it.
 */
export function isConfirmedOfflineLiveState({
  showCountdown = false,
  showStatus = false,
  status,
}: OfflineLiveStateInput = {}): boolean {
  if (showCountdown || showStatus) return true;
  if (status === undefined || status === null) return false;
  return status !== LiveStreamStatus.Unknown;
}

type StatusOverlayInput = {
  showStatus?: boolean;
  statusMessage?: string | null;
  posterArtworkFailed?: boolean;
  trailerActive?: boolean;
};

/**
 * Whether the offline message should be on screen.
 *
 * The server decides whether artwork is allowed to stand in for the message,
 * because that depends on the stream's status - an ended stream keeps its
 * message over a thumbnail. It cannot know whether the artwork actually
 * rendered, though: a thumbnail URL that 404s leaves the poster hidden with
 * nothing in its place, and the poster does not recover on later polls because
 * the URL never changes. So a message the server stepped aside from comes back
 * once the poster reports it has no artwork to show, which fails toward saying
 * something rather than showing an empty player.
 *
 * A visible trailer is still artwork, so it keeps the message away.
 */
export function shouldShowStatusOverlay({
  showStatus = false,
  statusMessage = '',
  posterArtworkFailed = false,
  trailerActive = false,
}: StatusOverlayInput = {}): boolean {
  if (showStatus) return true;
  if (!posterArtworkFailed || trailerActive) return false;
  return typeof statusMessage === 'string' && statusMessage.trim().length > 0;
}

export type ThumbnailPosterUpdate =
  | { action: 'keep' }
  | { action: 'set'; src: string }
  | { action: 'clear' };

/**
 * What the poster image should do with the thumbnail in a live state update.
 *
 * `undefined` carries no thumbnail information - a local re-sync reads the
 * controller's dataset, not the API - so the rendered poster has to survive it.
 * An explicitly empty value is the opposite: the stream has no thumbnail any
 * more, and the offline message comes back in that case, so a stale poster left
 * behind would put the message on top of artwork again.
 */
export function resolveThumbnailPosterUpdate(
  thumbnailUrl: string | null | undefined,
): ThumbnailPosterUpdate {
  if (thumbnailUrl === undefined) return { action: 'keep' };
  if (typeof thumbnailUrl !== 'string') return { action: 'clear' };

  const src = thumbnailUrl.trim();
  return src.length > 0 ? { action: 'set', src } : { action: 'clear' };
}
